import React from 'react';
import { Check, Minus } from 'lucide-react';
import { ContactButtons } from './ContactButtons';
import { useLanguage, Language } from '../contexts/LanguageContext';
type WhyPayStrings = {
  heading: string;
  introBefore: string;
  link: string;
  introAfter: string;
  ownTitle: string;
  ownSubtitle: string;
  own: string[];
  noorTitle: string;
  noorSubtitle: string;
  noor: string[];
  closing: string;
};
// EN/ES finalized. HT/VI = best effort, pending native review.
const T: Record<Language, WhyPayStrings> = {
  en: {
    heading: "Why pay if the government's form is free?",
    introBefore: "You're right — the U.S. government's passport forms are free, and you can file on your own at ",
    link: 'travel.state.gov',
    introAfter: '. The difference is how you do it. The government gives you the form in English and you fill it out alone. With Noor, you do it in your language, with help, and someone checks it before you submit.',
    ownTitle: 'On your own',
    ownSubtitle: 'Free at travel.state.gov',
    own: ['The form is in English only', 'No guidance — you fill it out alone', 'No one checks it before you submit', 'Small mistakes can lead to delays or rejection'],
    noorTitle: 'With Noor',
    noorSubtitle: 'In your language, with help',
    noor: ['Answer in your language — English, Spanish, Haitian Creole, or Vietnamese', 'Guided by phone, WhatsApp, or online', 'We prepare your official English form, ready to sign', 'A specialist reviews it to catch common mistakes', 'Printed in color and mailed to your home (with “with help” packages)'],
    closing: 'Either way, the government fee is the same and is paid directly to the U.S. Department of State. You pay Noor only for the help — in your language.'
  },
  es: {
    heading: '¿Por qué pagar si el formulario del gobierno es gratis?',
    introBefore: 'Tiene razón — los formularios de pasaporte del gobierno de EE. UU. son gratis, y usted puede tramitarlos por su cuenta en ',
    link: 'travel.state.gov',
    introAfter: '. La diferencia es cómo lo hace. El gobierno le da el formulario en inglés y usted lo llena solo. Con Noor, lo hace en su idioma, con ayuda, y alguien lo revisa antes de que usted lo entregue.',
    ownTitle: 'Por su cuenta',
    ownSubtitle: 'Gratis en travel.state.gov',
    own: ['El formulario está solo en inglés', 'Sin guía — lo llena usted solo', 'Nadie lo revisa antes de entregarlo', 'Un pequeño error puede causar demoras o rechazo'],
    noorTitle: 'Con Noor',
    noorSubtitle: 'En su idioma, con ayuda',
    noor: ['Responda en su idioma — inglés, español, creole haitiano o vietnamita', 'Guiado por teléfono, WhatsApp o en línea', 'Preparamos su formulario oficial en inglés, listo para firmar', 'Un especialista lo revisa para evitar errores comunes', 'Impreso a color y enviado a su casa (en los paquetes “con ayuda”)'],
    closing: 'De cualquier forma, la tarifa del gobierno es la misma y se paga directamente al Departamento de Estado de EE. UU. A Noor le paga solo por la ayuda — en su idioma.'
  },
  ht: {
    heading: 'Poukisa pou peye si fòm gouvènman an gratis?',
    introBefore: 'Ou gen rezon — fòm paspò gouvènman ameriken an gratis, epi ou ka ranpli yo poukont ou sou ',
    link: 'travel.state.gov',
    introAfter: '. Diferans lan se kijan ou fè li. Gouvènman an ba ou fòm nan an anglè epi ou ranpli li poukont ou. Ak Noor, ou fè li nan lang ou, ak èd, epi yon moun tcheke li anvan ou soumèt li.',
    ownTitle: 'Poukont ou',
    ownSubtitle: 'Gratis sou travel.state.gov',
    own: ['Fòm nan an anglè sèlman', 'Pa gen gid — ou ranpli li poukont ou', 'Pèsòn pa tcheke li anvan ou soumèt', 'Yon ti erè ka lakoz reta oswa rejè'],
    noorTitle: 'Ak Noor',
    noorSubtitle: 'Nan lang ou, ak èd',
    noor: ['Reponn nan lang ou — anglè, panyòl, kreyòl ayisyen, oswa vyetnamyen', 'Gidans pa telefòn, WhatsApp, oswa sou entènèt', 'Nou prepare fòm ofisyèl ou an anglè, pare pou siyen', 'Yon espesyalis tcheke li pou evite erè komen', 'Enprime an koulè epi voye lakay ou (ak pakè “ak èd” yo)'],
    closing: 'Nan tou de ka, frè gouvènman an se menm bagay la epi yo peye li dirèkteman bay Depatman Deta Etazini. Ou peye Noor sèlman pou èd la — nan lang ou.'
  },
  vi: {
    heading: 'Tại sao phải trả tiền nếu mẫu đơn của chính phủ miễn phí?',
    introBefore: 'Quý vị nói đúng — các mẫu đơn hộ chiếu của chính phủ Hoa Kỳ đều miễn phí, và quý vị có thể tự nộp tại ',
    link: 'travel.state.gov',
    introAfter: '. Khác biệt là ở cách quý vị thực hiện. Chính phủ cấp mẫu đơn bằng tiếng Anh và quý vị tự điền một mình. Với Noor, quý vị làm bằng ngôn ngữ của mình, có người hỗ trợ, và có người kiểm tra trước khi quý vị nộp.',
    ownTitle: 'Tự làm',
    ownSubtitle: 'Miễn phí tại travel.state.gov',
    own: ['Mẫu đơn chỉ có tiếng Anh', 'Không có hướng dẫn — quý vị tự điền một mình', 'Không ai kiểm tra trước khi nộp', 'Một lỗi nhỏ có thể gây chậm trễ hoặc bị từ chối'],
    noorTitle: 'Với Noor',
    noorSubtitle: 'Bằng ngôn ngữ của quý vị, có hỗ trợ',
    noor: ['Trả lời bằng ngôn ngữ của quý vị — tiếng Anh, Tây Ban Nha, Creole Haiti, hoặc tiếng Việt', 'Hướng dẫn qua điện thoại, WhatsApp hoặc trực tuyến', 'Chúng tôi chuẩn bị mẫu đơn chính thức bằng tiếng Anh, sẵn sàng để ký', 'Chuyên viên kiểm tra để tránh các lỗi thường gặp', 'In màu và gửi đến nhà quý vị (với các gói “có hỗ trợ”)'],
    closing: 'Dù bằng cách nào, phí chính phủ vẫn như nhau và được trả trực tiếp cho Bộ Ngoại giao Hoa Kỳ. Quý vị chỉ trả cho Noor phần hỗ trợ — bằng ngôn ngữ của mình.'
  }
};
export function WhyPay() {
  const {
    language
  } = useLanguage();
  const t = T[language];
  return <section className="py-24 bg-white border-t border-brand-border relative z-10" data-id="element-414" data-mp="2jda24xphka4x">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-415" data-mp="2t6ns2qnjjsfp">
        <div className="text-center max-w-3xl mx-auto mb-14" data-id="element-416" data-mp="2o7t8vnlnw1v2">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-textPrimary mb-5" data-id="element-417" data-mp="2o68fi1b32oys">
            {t.heading}
          </h2>
          <p className="text-lg text-brand-textSecondary leading-relaxed" data-id="element-418" data-mp="2eaet7q7kbh3t">
            {t.introBefore}
            <a href="https://travel.state.gov" target="_blank" rel="noopener noreferrer" className="text-brand-secondaryLink underline hover:text-brand-textPrimary transition-colors" data-id="element-419" data-mp="2a8tmynpja12l">
              {t.link}
            </a>
            {t.introAfter}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 items-start" data-id="element-420" data-mp="1vl8j2rku3xtm">
          {/* On your own */}
          <div className="bg-brand-bg/40 rounded-3xl p-8 border border-brand-border" data-id="element-421" data-mp="1qjj8fd1ixm62">
            <h3 className="font-heading font-bold text-xl text-brand-textPrimary mb-1" data-id="element-422" data-mp="1mj8zpsmckhni">
              {t.ownTitle}
            </h3>
            <p className="text-sm font-semibold text-brand-textSecondary uppercase tracking-wide mb-6" data-id="element-423" data-mp="2318iplkeut6">
              {t.ownSubtitle}
            </p>
            <ul className="space-y-4" data-id="element-424" data-mp="b2o8r82k5ukn">
              {t.own.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-425" data-mp="g0pqnpo8znuj" data-mpi={i}>
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white border border-brand-border flex items-center justify-center mt-0.5" data-id="element-426" data-mp="fy3hjllpl0a7">
                    <Minus className="w-4 h-4 text-brand-textSecondary" data-id="element-427" data-mp="3ji7uzn4tacq7" />
                  </div>
                  <span className="text-brand-textPrimary" data-id="element-428" data-mp="3of8281dedliw">{item}</span>
                </li>)}
            </ul>
          </div>

          {/* With Noor */}
          <div className="bg-white rounded-3xl p-8 border-2 border-brand-accent shadow-premium" data-id="element-429" data-mp="2vrvf9q3brbp2">
            <h3 className="font-heading font-bold text-xl text-brand-textPrimary mb-1" data-id="element-430" data-mp="30qf6a4wrzdoa">
              {t.noorTitle}
            </h3>
            <p className="text-sm font-semibold text-brand-accent uppercase tracking-wide mb-6" data-id="element-431" data-mp="1u4e621hwvion">
              {t.noorSubtitle}
            </p>
            <ul className="space-y-4" data-id="element-432" data-mp="1p6mb0ylyzgg8">
              {t.noor.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-433" data-mp="235cagkw9hs7r" data-mpi={i}>
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-bg flex items-center justify-center mt-0.5" data-id="element-434" data-mp="1y3wn2m6m39m7">
                    <Check className="w-4 h-4 text-brand-accent" data-id="element-435" data-mp="2d0nmgvgaclbt" />
                  </div>
                  <span className="text-brand-textPrimary" data-id="element-436" data-mp="282cc8jm2w8t2">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>

        <div className="max-w-3xl mx-auto text-center mt-12" data-id="element-437" data-mp="2ht7361a9rwku">
          <p className="text-brand-textSecondary leading-relaxed mb-8" data-id="element-438" data-mp="2htqj5cgd0aw8">
            {t.closing}
          </p>
          <div className="flex flex-col items-center gap-5" data-id="element-439" data-mp="2vqltd88ck8dv">
            <ContactButtons className="flex flex-col items-center" data-id="element-440" data-mp="2quy6x0l91tc2" />
          </div>
        </div>
      </div>
    </section>;
}